import { FilterGroup, ParamFilterState } from "../pages/home/heat-map/GlobalFilter";

type NumOp = "eq" | "gte" | "lte" | "between";
type SelOp = "eq" | "in";

type FilterRow =
  | { id: string; code: string; label: string; type: 1 | 3; op: NumOp; v1: number | null; v2: number | null }
  | { id: string; code: string; label: string; type: 2; op: SelOp; v1: string | string[] | null };

export const rowIsComplete = (row: FilterRow) => {
  if (!row.code) return false;
  if (row.type === 2) {
    if (row.op === "eq") return row.v1 !== null && row.v1 !== "";
    return Array.isArray(row.v1) && row.v1.length > 0;
  }
  if (row.op === "between") return row.v1 !== null && row.v2 !== null;
  return row.v1 !== null;
};

export const hasActiveFilter = (pf: ParamFilterState | null | undefined) => {
  const groups = pf?.groups ?? [];
  return groups.some(g => (g.rows ?? []).some(rowIsComplete));
};

export const getActiveGlobalParams = (storedData: any[], networkId: string) => {
  if (!Array.isArray(storedData)) return null;

  const active = storedData.find(item => (item?.checked === true && item?.id === networkId));
  if (!active) return null;
  return active;
};

export const matchRow = (params: Record<string, any> | null, r: FilterRow) => {
  if (!params) return false;

  const raw = params[r.code];
  if (raw === undefined || raw === null || raw === "") return false;

  if (r.type === 2) {
    const pv = String(raw);
    if (r.op === "eq") return pv === String(r.v1);
    const arr = Array.isArray(r.v1) ? r.v1.map(String) : [];
    return arr.includes(pv);
  }

  const pn = Number(raw);
  if (Number.isNaN(pn)) return false;

  if (r.op === "eq") return pn === Number(r.v1);
  if (r.op === "gte") return pn >= Number(r.v1);
  if (r.op === "lte") return pn <= Number(r.v1);

  const a = Number(r.v1), b = Number(r.v2);
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  const min = Math.min(a, b), max = Math.max(a, b);
  return pn >= min && pn <= max;
};

export const matchGroup = (params: Record<string, any> | null, group: FilterGroup) => {
  const rows = (group.rows ?? []).filter(rowIsComplete);
  if (!rows.length) return true;

  return group.logic === "OR"
    ? rows.some(r => matchRow(params, r))
    : rows.every(r => matchRow(params, r));
};

export const matchesFilter = (params: Record<string, any> | null, filter: ParamFilterState | null) => {
  if (!filter?.groups?.length) return true;

  const groups = filter.groups.filter(g => (g.rows ?? []).some(rowIsComplete));
  if (!groups.length) return true;

  return filter.logic === "OR"
    ? groups.some(g => matchGroup(params, g))
    : groups.every(g => matchGroup(params, g));
};

export const buildCoordPassSet = (
  allCoords: string[],
  storedLocationParams: Record<string, Record<string, any>>,
  globalParams: Record<string, any> | null,
  filter: ParamFilterState | null
) => {
  if (!hasActiveFilter(filter)) return null as Set<string> | null;

  const pass = new Set<string>();
  for (const coordKey of allCoords) {
    const individual = storedLocationParams[coordKey];
    const effectiveParams = individual ?? globalParams;

    if (matchesFilter(effectiveParams, filter)) {
      pass.add(coordKey);
    }
  }

  return pass;
};